import type { HTMLAttributes, MouseEvent, ReactNode } from "react";
import { useLocation } from "wouter";

/**
 * A home card that opens its listing wherever it is clicked, not only on its
 * title or button. Links, buttons and fields inside it keep their own action
 * (a "Check dates" button still opens the booking lightbox), selecting text
 * does not navigate, and Ctrl/Cmd-click opens the listing in a new tab. The
 * card's title stays a real link, so keyboards and crawlers reach it as before.
 */
export function ClickableCard({
  href,
  as: Tag = "li",
  className = "",
  children,
  onNavigate,
  ...rest
}: {
  href: string;
  as?: "li" | "div" | "article";
  className?: string;
  children: ReactNode;
  onNavigate?: () => void;
} & HTMLAttributes<HTMLElement>) {
  const [, setLocation] = useLocation();
  const onClick = (e: MouseEvent<HTMLElement>) => {
    if (e.defaultPrevented || e.button !== 0) return;
    const target = e.target as HTMLElement;
    if (target.closest('a, button, input, select, textarea, label, [role="button"], [data-card-ignore]')) return;
    if (typeof window !== "undefined" && window.getSelection()?.toString()) return;
    if (e.metaKey || e.ctrlKey) {
      window.open(href, "_blank", "noopener");
      return;
    }
    onNavigate?.();
    setLocation(href);
  };
  return (
    <Tag {...rest} onClick={onClick} className={`cursor-pointer ${className}`}>
      {children}
    </Tag>
  );
}
